/**
 * External tasks v1 (260702) bod 3 — Approve / Return-for-fix actions for the
 * external-audience TaskDetailSlideOver.
 *
 * Only rendered when the task is assigned to the current external user. Both
 * decisions reassign the task to the internal project PM server-side (no kanban
 * column move in v1). "Return for fix" opens an inline required-reason input
 * before submit; "Approve" submits immediately (reason optional, omitted here).
 * On success the task leaves the external user's filtered view (now the PM's).
 */
import { useState, useCallback } from 'react';
import { Button } from '../common/Button';
import { useExternalReview } from '../../hooks/useExternalReview';
import { useToast } from '../ui/Toast';

export interface ExternalReviewActionsProps {
  taskId: number;
  projectId: number;
}

const REASON_MAX = 2000;

export function ExternalReviewActions({ taskId, projectId }: ExternalReviewActionsProps) {
  const review = useExternalReview();
  const showToast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const submit = useCallback(
    (decision: 'approved' | 'rejected', reasonText?: string) => {
      review.mutate(
        { taskId, projectId, decision, reason: reasonText },
        {
          onError: (err) => {
            const msg = err instanceof Error ? err.message : 'Submission failed';
            showToast(msg, 'error');
          },
          onSuccess: () => {
            showToast(
              decision === 'approved' ? 'Approved — sent to the project manager' : 'Returned for fix',
              'success',
            );
            setRejecting(false);
            setReason('');
          },
        },
      );
    },
    [review, taskId, projectId, showToast],
  );

  const reasonTrimmed = reason.trim();
  const canSubmitReject =
    reasonTrimmed.length > 0 && reasonTrimmed.length <= REASON_MAX && !review.isPending;

  return (
    <div
      className="mt-4 rounded-lg border border-ms-border bg-ms-canvas p-3"
      data-testid="external-review-actions"
    >
      <div className="text-sm font-medium text-ms-text-strong mb-2">Review</div>
      {!rejecting ? (
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={() => submit('approved')}
            disabled={review.isPending}
            data-testid="external-review-approve"
          >
            Approve
          </Button>
          <Button
            variant="secondary"
            onClick={() => setRejecting(true)}
            disabled={review.isPending}
            data-testid="external-review-return"
          >
            Return for fix
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <label
            htmlFor="external-review-reason"
            className="block text-xs font-medium text-ms-text-secondary"
          >
            Reason (required)
          </label>
          <textarea
            id="external-review-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={REASON_MAX}
            rows={3}
            className="w-full text-sm px-2 py-1.5 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
            placeholder="Explain what needs fixing…"
            data-testid="external-review-reason"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => submit('rejected', reasonTrimmed)}
              disabled={!canSubmitReject}
              data-testid="external-review-return-submit"
            >
              Submit
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setRejecting(false);
                setReason('');
              }}
              disabled={review.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
